import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Trash01 as Trash01Icon } from "@remo-co/ui-core/src/icons/Trash01";
import { useI18n } from "i18n";
import { Link } from "@remo-co/ui-core/src/components/Link";
import { useSelector } from "react-redux";
import {
  selectCustomDomain,
  selectIsCustomDomainVerified,
} from "modules/companySettings/redux/selectors";
import {
  deleteCustomDomain,
  updateCompanySettings,
} from "services/apiService/apis";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useCallback, useContext, useState } from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import useCompany from "modules/company/hooks/useCompany";
import { selectUserId } from "modules/auth/redux/selectors";
import { checkCName } from "helpers/dns";
import { useStyles } from "./styles";

interface Props {
  enabledAgencyWhiteLabelling: boolean;
}

export const CustomDomain = ({ enabledAgencyWhiteLabelling }: Props) => {
  const { company } = useCompany();
  const userId = useSelector(selectUserId);
  const customDomain = useSelector(selectCustomDomain);
  const isCustomDomainVerified = useSelector(selectIsCustomDomainVerified);
  const { setCompanySettings, resetCompanySettings } =
    useCompanySettingsActions();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const [isCheckLoading, setIsCheckLoading] = useState(false);
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["settings", "url", "common"]);
  const styles = useStyles();

  const onRequestClick = () => {
    if (company && company.id && userId) {
      track(Events.CUSTOM_DOMAIN_REQUESTED, {
        companyIds: company.id,
        userId,
      });
    }
  };

  const onEditClick = () => {
    if (company && company.id && userId) {
      track(Events.CUSTOM_DOMAIN_EDIT_REQUESTED, {
        companyIds: company.id,
        userId,
      });
    }
  };

  const deleteDomain = useCallback(async () => {
    if (company && company.id && userId) {
      const res = await deleteCustomDomain(company.id);

      if (!res.isSuccess) {
        addErrorNotification({
          message: t("settings:custom.domain.delete.error"),
        });

        return;
      }

      if (res.companySettings) {
        resetCompanySettings();
        setCompanySettings(res.companySettings);
      }
      track(Events.CUSTOM_DOMAIN_REMOVED, {
        companyIds: company.id,
        userId,
      });
      addSuccessNotification({
        message: t("settings:custom.domain.delete.success"),
      });
    }
  }, [
    company,
    track,
    addSuccessNotification,
    t,
    addErrorNotification,
    resetCompanySettings,
    setCompanySettings,
    userId,
  ]);

  const onCheckDomain = async () => {
    if (company && company.id && userId) {
      setIsCheckLoading(true);
      track(Events.CUSTOM_DOMAIN_CHECKED, {
        companyIds: company.id,
        userId,
      });

      const hasValidCName = await checkCName(customDomain);

      if (hasValidCName) {
        const res = await updateCompanySettings(company.id, {
          isCustomDomainVerified: true,
          company: company.id,
        });

        if (!res.isSuccess) {
          addErrorNotification({
            message: t("settings:custom.domain.check.error"),
          });

          return;
        }

        if (res.companySettings) {
          setCompanySettings(res.companySettings);
        }
        addSuccessNotification({
          message: t("settings:custom.domain.check.success"),
        });
        setIsCheckLoading(false);
        return;
      }

      setIsCheckLoading(false);
      addErrorNotification({
        message: t("settings:custom.domain.check.error"),
      });
    }
  };

  return (
    <Box className={styles.container}>
      <Typography variant="h5" color="textPrimary">
        {t("settings:custom.domain")}
      </Typography>
      <Typography
        variant="body1"
        color="textPrimary"
        className={styles.subtitle}
      >
        {t("settings:custom.domain.subtitle")}
      </Typography>
      <Box className={styles.content}>
        {!customDomain ? (
          <Button<"a">
            variant="secondary"
            color="blue"
            size="md"
            data-testid="request-custom-domain-button"
            href={t("url:request.custom.domain.email.sender.link")}
            disabled={!enabledAgencyWhiteLabelling}
            onClick={onRequestClick}
          >
            {t("settings:request.custom.domain")}
          </Button>
        ) : (
          <>
            <Input
              size="sm"
              value={customDomain}
              disabled
              className={styles.customDomainInput}
            />
            {!isCustomDomainVerified && (
              <Button
                variant="text"
                color="dark"
                size="sm"
                data-testid="check-custom-domain-button"
                disabled={!enabledAgencyWhiteLabelling}
                className={styles.checkButton}
                loading={isCheckLoading}
                onClick={onCheckDomain}
              >
                {t("settings:button.check")}
              </Button>
            )}
            <Button<"a">
              variant="text"
              color="dark"
              size="sm"
              data-testid="edit-custom-domain-button"
              href={t("url:edit.custom.domain.mailto")}
              disabled={!enabledAgencyWhiteLabelling}
              className={styles.editButton}
              contentClassName={styles.editButtonContent}
              onClick={onEditClick}
            >
              {t("common:button.edit")}
            </Button>
            <Button
              disabled={!enabledAgencyWhiteLabelling}
              isIconButton
              variant="warning"
              color="dark"
              size="sm"
              onClick={deleteDomain}
              data-testid="delete-custom-domain-button"
              className={styles.deleteButton}
            >
              <Trash01Icon fontSize="large" />
            </Button>
          </>
        )}
      </Box>
      <Link
        openInNewTab
        href={t("url:custom.domain.help.article")}
        data-testid="custom-domain-help-link"
        aria-label={t("settings:custom.domain.help.link")}
      >
        <Typography
          variant="body1"
          component="span"
          underline
          className={styles.helpLink}
        >
          {t("settings:custom.domain.help.link")}
        </Typography>
      </Link>
    </Box>
  );
};
